import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Box,
  Flex,
  Text,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useColorModeValue,
  useDisclosure,
  Button,
  SimpleGrid,
  VStack,
  Select,
  HStack,
  Badge,
  Icon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tooltip,
} from "@chakra-ui/react";
import { AiOutlineDown, AiOutlineEdit, AiOutlineDelete } from "react-icons/ai";
import { FaSort } from "react-icons/fa";
import { QuestionIcon } from "@chakra-ui/icons";
import Card from "../../../../components/Card/Card";
import CardBody from "../../../../components/Card/CardBody";
import { Signal } from "./SignalCard";
import SignalCard from "./SignalCard";
import HowToUseModal from "./HowToUseModal";
import CurrentSession from "./CurrentSession";
import MarketOverviewCard from "./MarketOverviewCard";
import SignalLoadingState from "./SignalLoadingState";
import { useSignalsContextOptional } from "../../../../contexts/SignalsContext";
import { useDisplayTimeZone } from "../../../../contexts/DisplayTimezoneContext";
import { useDisplayCurrency } from "../../../../contexts/DisplayCurrencyContext";
import { formatTradeDateTimeForDisplay } from "../../../../utils/displayDateTime";
import type {
  SystemPerformanceData,
  SystemPerformanceWindow,
  SystemPerformanceByPeriod,
  SystemPerformanceBestWorstItem,
} from "../../../../types/signals";
import { formatInstrumentSymbol } from "../../../../services/signalMapper";
import { tradeConflictsWithBrokerAccount } from "../../../../utils/brokerAccountScope";

export interface AssetData {
  symbol: string;
  icon?: React.ReactNode;
  price: string;
  chartData: number[];
  baseValue?: number;
}

interface SignalStreamCardProps {
  asset: AssetData;
  signals: Signal[];
  onPlaceOrder?: (
    signal: Signal,
    asset: AssetData,
    overrides?: {
      lotSize?: number;
      stopLossPrice?: number;
      takeProfitPrice?: number;
    }
  ) => void;
  onSignalClick?: (signal: Signal, asset: AssetData) => void;
  onRemove?: (asset: AssetData) => void;
  onCloseTrade?: (tradeId: string) => void;
  onCloseAllTrades?: () => void | Promise<void>;
  brokerAccountCode?: string | null;
  latestNoSignalReason?: string | null; // Latest reason from no_signal WebSocket messages
  systemPerformance?: SystemPerformanceData | null; // System performance (API + WebSocket)
}

// Risk/Reward filter options
const RR_FILTER_OPTIONS = [
  { value: "all", label: "All Risk/Reward Ratios" },
  { value: "1-2", label: "1:1 - 1:2" },
  { value: "2-3", label: "1:2 - 1:3" },
  { value: "3-4", label: "1:3 - 1:4" },
  { value: "4+", label: "1:4+" },
];

// Trade Type filter options
const TRADE_TYPE_FILTER_OPTIONS = [
  { value: "all", label: "All Types" },
  { value: "LONG", label: "Long" },
  { value: "SHORT", label: "Short" },
];

// Session key → UTC hours (for "By session" table labels)
const SESSION_UTC_HOURS: Record<string, string> = {
  sydney: "22:00–07:00+1d",
  tokyo: "00:00–09:00",
  london: "08:00–17:00",
  new_york: "13:00–22:00",
  london_tokyo: "08:00–09:00",
  london_new_york: "13:00–17:00",
  sydney_tokyo: "00:00–07:00",
  sydney_london: "22:00–08:00",
  tokyo_new_york: "13:00–09:00+1d",
};

/** Convert UTC time range string to user's local time; returns { local, utc } for display and tooltip */
function utcTimeRangeToLocal(utcRange: string): { local: string; utc: string } {
  const hasNextDay = utcRange.includes("+1d");
  const clean = utcRange.replace(/\s*\+1d\s*$/, "");
  const m = clean.match(/^(\d{1,2}):(\d{2})–(\d{1,2}):(\d{2})$/);
  if (!m) return { local: utcRange, utc: utcRange + (utcRange.endsWith("UTC") ? "" : " UTC") };
  const [, sh, sm, eh, em] = m;
  const startUtc = new Date(Date.UTC(2000, 0, 1, parseInt(sh, 10), parseInt(sm, 10), 0));
  const endUtc = new Date(Date.UTC(2000, 0, hasNextDay ? 2 : 1, parseInt(eh, 10), parseInt(em, 10), 0));
  const opts: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit", hour12: false };
  const startLocal = startUtc.toLocaleTimeString(undefined, opts);
  const endLocal = endUtc.toLocaleTimeString(undefined, opts);
  const localStr = hasNextDay ? `${startLocal}–${endLocal}+1d` : `${startLocal}–${endLocal}`;
  return { local: localStr, utc: utcRange + " UTC" };
}

/** Convert "HH:MM UTC" hour label to local time; returns { local, utc } for display and tooltip */
function hourUtcLabelToLocal(label: string): { local: string; utc: string } | null {
  const m = label.match(/^(\d{1,2}):(\d{2})\s*UTC$/i);
  if (!m) return null;
  const [, h, min] = m;
  const utcDate = new Date(Date.UTC(2000, 0, 1, parseInt(h, 10), parseInt(min, 10), 0));
  const local = utcDate.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return { local, utc: label };
}

// Parse RR value from string format "1:X.X" to number
const parseRR = (rrString: string): number => {
  const match = rrString.match(/1:([\d.]+)/);
  if (match) {
    return parseFloat(match[1]);
  }
  return 0;
};

// Filter signals by risk/reward ratio
const filterSignalsByRR = (signals: Signal[], filterValue: string): Signal[] => {
  if (filterValue === "all") {
    return signals;
  }

  return signals.filter((signal) => {
    const rrValue = parseRR(signal.rr);
    switch (filterValue) {
      case "1-2":
        return rrValue >= 1 && rrValue < 2;
      case "2-3":
        return rrValue >= 2 && rrValue < 3;
      case "3-4":
        return rrValue >= 3 && rrValue < 4;
      case "4+":
        return rrValue >= 4;
      default:
        return true;
    }
  });
};

// Format percentage for P&L / drawdown (signed)
const formatPct = (value: number): string => {
  const sign = value >= 0 ? "+" : "";
  return `${sign}${value.toFixed(2)}%`;
};

const SignalStreamCard: React.FC<SignalStreamCardProps> = ({
  asset,
  signals,
  onPlaceOrder,
  onSignalClick,
  onRemove,
  onCloseTrade,
  onCloseAllTrades,
  brokerAccountCode = null,
  latestNoSignalReason = null,
  systemPerformance = null,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const displayTimeZone = useDisplayTimeZone();
  const { formatMoney } = useDisplayCurrency();

  // Global signals context (WebSocket + enablement state)
  const signalsContext = useSignalsContextOptional();

  // Signals enablement and countdown come from context so WebSocket state
  // and UI stay in sync across the app.
  const signalsEnabled = signalsContext?.signalsEnabled ?? false;
  const timeRemainingSeconds = signalsContext?.timeRemainingSeconds ?? null;
  const signalsLoading = signalsContext?.signalsLoading ?? false;
  const enableSignals = signalsContext?.enableSignals ?? (async () => {});
  const disableSignals = signalsContext?.disableSignals ?? (async () => {});
  const formatTimeRemaining =
    signalsContext?.formatTimeRemaining ?? ((seconds: number | null) => "00:00");

  // Taken setups (trades created from Tradar signals), scoped to this asset symbol
  const allTakenTrades = signalsContext?.takenTrades ?? {};
  const takenTradesForAsset = useMemo(() => {
    const targetSymbol = asset.symbol;
    return Object.values(allTakenTrades).filter((trade) => {
      if (tradeConflictsWithBrokerAccount(trade, brokerAccountCode)) return false;
      // Normalize backend trade symbols (e.g. "XAUUSD" -> "XAU/USD")
      const normalizedTradeSymbol = formatInstrumentSymbol(trade.symbol);
      return normalizedTradeSymbol === targetSymbol;
    });
  }, [allTakenTrades, asset.symbol]);

  // Filter to only show active/open trades, sorted by entry date descending (newest first)
  const activeTrades = useMemo(() => {
    const open = takenTradesForAsset.filter((trade) => trade.status === "open");
    return [...open].sort((a, b) => {
      const tA = new Date(a.entryDate).getTime();
      const tB = new Date(b.entryDate).getTime();
      return tB - tA;
    });
  }, [takenTradesForAsset]);

  const ACTIVE_SETUPS_PAGE_SIZE = 10;
  const [activeSetupsPage, setActiveSetupsPage] = useState(1);
  const paginatedActiveTrades = useMemo(() => {
    const start = (activeSetupsPage - 1) * ACTIVE_SETUPS_PAGE_SIZE;
    return activeTrades.slice(start, start + ACTIVE_SETUPS_PAGE_SIZE);
  }, [activeTrades, activeSetupsPage]);
  const activeSetupsTotalPages = Math.max(1, Math.ceil(activeTrades.length / ACTIVE_SETUPS_PAGE_SIZE));

  const activeSetupsTotals = useMemo(() => {
    let unrealised = 0;
    let lossPotential = 0;
    let profitPotential = 0;
    activeTrades.forEach((t) => {
      const pnl = t.floatingPnl ?? t.netPnL;
      if (pnl != null) unrealised += pnl;
      if (t.lossPotential != null) lossPotential += t.lossPotential;
      if (t.profitPotential != null) profitPotential += t.profitPotential;
    });
    return { unrealised, lossPotential, profitPotential };
  }, [activeTrades]);

  /** Open trades taken from each signal (by eventId) for "Taken × N" and card shade */
  const takenCountByEventId = useMemo(() => {
    const map: Record<string, number> = {};
    activeTrades.forEach((t) => {
      const eid = (t as { signalEventId?: string }).signalEventId;
      if (eid) {
        map[eid] = (map[eid] ?? 0) + 1;
      }
    });
    return map;
  }, [activeTrades]);

  useEffect(() => {
    if (activeSetupsPage > activeSetupsTotalPages) {
      setActiveSetupsPage(1);
    }
  }, [activeTrades.length, activeSetupsPage, activeSetupsTotalPages]);

  const [rrFilter, setRrFilter] = useState<string>("all");
  const [tradeTypeFilter, setTradeTypeFilter] = useState<string>("all");
  
  const cardBg = useColorModeValue("white", "#151517");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const hoverBorderColor = useColorModeValue("rgba(56, 187, 209, 0.4)", "rgba(56, 187, 209, 0.6)");
  const textColor = useColorModeValue("#575757", "white");
  const labelColor = useColorModeValue("gray.400", "gray.400");
  const menuBg = useColorModeValue("white", "#1A202C");
  const menuButtonHoverBg = useColorModeValue("rgba(56, 187, 209, 0.1)", "rgba(56, 187, 209, 0.2)");
  const scrollbarThumb = useColorModeValue("#4A5568", "#718096");
  const scrollbarThumbHover = useColorModeValue("#718096", "#A0AEC0");
  
  // Action bar colors matching Trade Log
  const actionBarBg = useColorModeValue("white", "#151517");
  const actionBarBorderColor = useColorModeValue("gray.200", "gray.600");
  const secondaryTextColor = useColorModeValue("gray.400", "white");
  const buttonHoverBg = useColorModeValue("gray.100", "gray.700");
  const menuHoverBg = useColorModeValue("gray.50", "gray.700");
  const alternateRowBg = useColorModeValue("gray.50", "gray.900");
  const tooltipBg = useColorModeValue("gray.800", "gray.700");
  const tooltipColor = useColorModeValue("white", "white");

  const renderSystemPerformancePeriod = (title: string, w: SystemPerformanceWindow | null) => {
    // Treat periods with 0 trades as "no data" and render "-" for all metrics
    const hasData = w != null && typeof w.trades_count === "number" && w.trades_count > 0;

    const winRatePct =
      hasData &&
      typeof w!.win_rate_pct === "number" &&
      !Number.isNaN(w!.win_rate_pct)
        ? w!.win_rate_pct > 100
          ? w!.win_rate_pct / 100
          : w!.win_rate_pct
        : null;
    const winRate = winRatePct != null ? `${winRatePct.toFixed(1)}%` : "-";

    const totalPnl =
      hasData &&
      typeof w!.total_pnl_pct === "number" &&
      !Number.isNaN(w!.total_pnl_pct)
        ? formatPct(w!.total_pnl_pct)
        : "-";

    const trades = hasData ? String(w!.trades_count) : "-";

    const wlRatio =
      hasData &&
      typeof w!.w_l_ratio === "number" &&
      !Number.isNaN(w!.w_l_ratio)
        ? w!.w_l_ratio.toFixed(2)
        : "-";

    const maxDd =
      hasData &&
      w!.max_drawdown_percent != null &&
      !Number.isNaN(w!.max_drawdown_percent)
        ? `${w!.max_drawdown_percent.toFixed(1)}%`
        : "-";
    return (
      <Box
        key={title}
        border="1px solid"
        borderColor={borderColor}
        borderRadius="md"
        p={3}
        bg={cardBg}
      >
        <Text
          fontSize="xs"
          fontWeight="medium"
          color={secondaryTextColor}
          fontFamily="'IBM Plex Sans', sans-serif"
          textTransform="uppercase"
          mb={3}
        >
          {title}
        </Text>
        <SimpleGrid columns={5} spacing={3}>
          <Box>
            <Text fontSize="xs" color={labelColor} fontFamily="'IBM Plex Sans', sans-serif" mb={1}>
              Win Rate
            </Text>
            <Text fontSize="lg" fontWeight="bold" color={textColor} fontFamily="'IBM Plex Sans', sans-serif">
              {winRate}
            </Text>
          </Box>
          <Box>
            <Text fontSize="xs" color={labelColor} fontFamily="'IBM Plex Sans', sans-serif" mb={1}>
              Total P&L
            </Text>
            <Text fontSize="lg" fontWeight="bold" color={textColor} fontFamily="'IBM Plex Sans', sans-serif">
              {totalPnl}
            </Text>
          </Box>
          <Box>
            <Text fontSize="xs" color={labelColor} fontFamily="'IBM Plex Sans', sans-serif" mb={1}>
              Trades
            </Text>
            <Text fontSize="lg" fontWeight="bold" color={textColor} fontFamily="'IBM Plex Sans', sans-serif">
              {trades}
            </Text>
          </Box>
          <Box>
            <Text fontSize="xs" color={labelColor} fontFamily="'IBM Plex Sans', sans-serif" mb={1}>
              W/L Ratio
            </Text>
            <Text fontSize="lg" fontWeight="bold" color={textColor} fontFamily="'IBM Plex Sans', sans-serif">
              {wlRatio}
            </Text>
          </Box>
          <Box>
            <Text fontSize="xs" color={labelColor} fontFamily="'IBM Plex Sans', sans-serif" mb={1}>
              Max Drawdown
            </Text>
            <Text fontSize="lg" fontWeight="bold" color={textColor} fontFamily="'IBM Plex Sans', sans-serif">
              {maxDd}
            </Text>
          </Box>
        </SimpleGrid>
      </Box>
    );
  };

  const formatBestWorstItem = (item: SystemPerformanceBestWorstItem) => {
    const hasData = item != null && typeof item.trades_count === "number" && item.trades_count > 0;
    const winRatePct =
      hasData && typeof item.win_rate_pct === "number" && !Number.isNaN(item.win_rate_pct)
        ? item.win_rate_pct > 100
          ? item.win_rate_pct / 100
          : item.win_rate_pct
        : null;
    const winRate = winRatePct != null ? `${winRatePct.toFixed(1)}%` : "-";
    const totalPnl =
      hasData && typeof item.total_pnl_pct === "number" && !Number.isNaN(item.total_pnl_pct)
        ? formatPct(item.total_pnl_pct)
        : "-";
    const trades = hasData ? String(item.trades_count) : "-";
    const wlRatio =
      hasData && typeof item.w_l_ratio === "number" && !Number.isNaN(item.w_l_ratio)
        ? item.w_l_ratio.toFixed(2)
        : "-";
    const maxDd =
      hasData &&
      item.max_drawdown_percent != null &&
      !Number.isNaN(item.max_drawdown_percent)
        ? `${item.max_drawdown_percent.toFixed(1)}%`
        : "-";
    return { label: item.label, winRate, totalPnl, trades, wlRatio, maxDd };
  };

  const renderBestWorstTable = (
    title: string,
    items: SystemPerformanceBestWorstItem[],
    paramColumnLabel: string
  ) => {
    if (!items?.length) return null;
    return (
      <Box mb={4}>
        <Text
          fontSize="xs"
          fontWeight="medium"
          color={labelColor}
          fontFamily="'IBM Plex Sans', sans-serif"
          mb={2}
        >
          {title}
        </Text>
        <Box overflowX="auto">
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium" fontSize="xs">
                  {paramColumnLabel}
                </Th>
                <Th color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium" fontSize="xs" isNumeric>
                  Win Rate
                </Th>
                <Th color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium" fontSize="xs" isNumeric>
                  Total P&L
                </Th>
                <Th color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium" fontSize="xs" isNumeric>
                  Trades
                </Th>
                <Th color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium" fontSize="xs" isNumeric>
                  W/L Ratio
                </Th>
                <Th color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium" fontSize="xs" isNumeric>
                  Max Drawdown
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {items.map((item) => {
                const f = formatBestWorstItem(item);
                const isSession = paramColumnLabel === "Session";
                const isHour = paramColumnLabel === "Hour";
                const hoursUtc = isSession ? SESSION_UTC_HOURS[String(item.key)] : null;
                const { local: hoursLocal, utc: hoursUtcLabel } =
                  hoursUtc ? utcTimeRangeToLocal(hoursUtc) : { local: "", utc: "" };
                const hourConverted = isHour ? hourUtcLabelToLocal(f.label) : null;
                let paramDisplay: string;
                let paramTooltip: string | undefined;
                if (isSession && hoursUtc) {
                  paramDisplay = `${f.label} (${hoursLocal})`;
                  paramTooltip = `UTC: ${hoursUtcLabel}`;
                } else if (isHour && hourConverted) {
                  paramDisplay = hourConverted.local;
                  paramTooltip = hourConverted.utc;
                } else {
                  paramDisplay = f.label;
                  paramTooltip = undefined;
                }
                return (
                  <Tr key={String(item.key)}>
                    <Td fontSize="xs" fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium" color={textColor}>
                      {paramTooltip ? (
                        <Tooltip label={paramTooltip} hasArrow placement="top" fontSize="xs">
                          <span style={{ borderBottom: "1px dotted currentColor", cursor: "help" }}>
                            {paramDisplay}
                          </span>
                        </Tooltip>
                      ) : (
                        paramDisplay
                      )}
                    </Td>
                    <Td fontSize="xs" fontFamily="'IBM Plex Sans', sans-serif" color={textColor} isNumeric>
                      {f.winRate}
                    </Td>
                    <Td fontSize="xs" fontFamily="'IBM Plex Sans', sans-serif" color={textColor} isNumeric>
                      {f.totalPnl}
                    </Td>
                    <Td fontSize="xs" fontFamily="'IBM Plex Sans', sans-serif" color={textColor} isNumeric>
                      {f.trades}
                    </Td>
                    <Td fontSize="xs" fontFamily="'IBM Plex Sans', sans-serif" color={textColor} isNumeric>
                      {f.wlRatio}
                    </Td>
                    <Td fontSize="xs" fontFamily="'IBM Plex Sans', sans-serif" color={textColor} isNumeric>
                      {f.maxDd}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      </Box>
    );
  };

  const renderBestWorstSection = (
    sectionTitle: string,
    paramColumnLabel: string,
    data: SystemPerformanceByPeriod | null | undefined
  ) => {
    if (!data || (!data.best?.length && !data.worst?.length)) return null;
    return (
      <Box
        key={sectionTitle}
        border="1px solid"
        borderColor={borderColor}
        borderRadius="md"
        p={3}
        bg={cardBg}
      >
        <Text
          fontSize="xs"
          fontWeight="medium"
          color={secondaryTextColor}
          fontFamily="'IBM Plex Sans', sans-serif"
          textTransform="uppercase"
          mb={3}
        >
          {sectionTitle} (last {data.period_weeks} weeks)
        </Text>
        <VStack align="stretch" spacing={0}>
          {renderBestWorstTable("Best", data.best || [], paramColumnLabel)}
          {renderBestWorstTable("Worst", data.worst || [], paramColumnLabel)}
        </VStack>
      </Box>
    );
  };

  // Filter signals based on selected risk/reward ratio and trade type
  const filteredSignals = useMemo(() => {
    let filtered = filterSignalsByRR(signals, rrFilter);
    
    // Apply trade type filter
    if (tradeTypeFilter !== "all") {
      filtered = filtered.filter((signal) => signal.type === tradeTypeFilter);
    }
    
    return filtered;
  }, [signals, rrFilter, tradeTypeFilter]);

  // Handle start button click
  const handleStartClick = async () => {
    if (!signalsEnabled) {
      await enableSignals();
    } else {
      await disableSignals();
    }
  };

  return (
    // @ts-ignore - Chakra UI Card component with responsive props has complex union types
    <Card
      w="100%"
      pt={{ base: '4px', sm: '6px', md: '8px' }}
      px="0"
      pb="0"
      bg={cardBg}
      borderRadius="15px"
      display="flex"
      flexDirection="column"
      gap={0}
      h="100%"
    >
      {/* Action Bar */}
      <Box
        bg={actionBarBg}
        borderRadius="lg"
        border="1px solid"
        borderColor={actionBarBorderColor}
        p={{ base: 3, md: 4 }}
        mx={4}
        mb={4}
        flexShrink={0}
      >
        {/* @ts-ignore - Chakra UI Flex component with responsive props creates complex union types */}
        <Flex
          direction={{ base: "column", md: "row" }}
          gap={4}
          align={{ base: "stretch", md: "center" }}
          justify="space-between"
        >
          {/* Left: Asset Icon/Selector and Filters */}
          <Box flex={{ base: "none", md: "1" }} w="100%">
            <HStack spacing={3} flexWrap="wrap">
              {/* Asset Icon and Selector */}
              {asset.icon && (
                <Box w="32px" h="32px" flexShrink={0}>
                  {asset.icon}
                </Box>
              )}
              
              <Menu>
                {/* @ts-ignore - Chakra UI MenuButton with responsive props creates complex union types */}
                <MenuButton
                  as={Button}
                  variant="ghost"
                  size="sm"
                  bg="transparent"
                  w="auto"
                  h="auto"
                  px={0}
                  py={0}
                  minW="auto"
                  fontSize="md"
                  fontWeight="bold"
                  color={textColor}
                  fontFamily="'IBM Plex Sans', sans-serif"
                  _hover={{ bg: "transparent" }}
                  _active={{ bg: "transparent" }}
                  _focus={{ bg: "transparent", boxShadow: "none" }}
                  rightIcon={<Icon as={AiOutlineDown} w={4} h={4} color={textColor} />}
                >
                  {asset.symbol}
                </MenuButton>
                <MenuList maxH="200px" overflowY="auto" bg={menuBg} borderColor={borderColor}>
                  <MenuItem
                    onClick={() => {}}
                    fontSize="sm"
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    color={textColor}
                    bg={menuBg}
                    _hover={{ bg: menuHoverBg }}
                  >
                    XAU/USD
                  </MenuItem>
                </MenuList>
              </Menu>
              
              {/* Risk/Reward Filter */}
              {/* Risk/Reward Filter */}
              <Menu>
                {/* @ts-ignore - Chakra UI MenuButton with responsive props creates complex union types */}
                <MenuButton
                  as={Button}
                  size="sm"
                  bg={actionBarBg}
                  w={{ base: "100%", md: "200px" } as any}
                  h="32px"
                  px="12px"
                  py="8px"
                  fontSize="xs"
                  fontFamily="'IBM Plex Sans', sans-serif"
                  fontWeight="medium"
                  color={secondaryTextColor}
                  textTransform="uppercase"
                  border="1px solid"
                  borderColor={actionBarBorderColor}
                  borderRadius="md"
                  textAlign="left"
                  justifyContent="space-between"
                  _hover={{ bg: buttonHoverBg }}
                  _active={{ bg: actionBarBg }}
                  _focus={{ bg: actionBarBg, boxShadow: "0 0 0 1px #3EEAEC" }}
                  rightIcon={<Icon as={AiOutlineDown} w={4} h={4} color={secondaryTextColor} />}
                >
                  {RR_FILTER_OPTIONS.find(opt => opt.value === rrFilter)?.label || "All Risk/Reward Ratios"}
                </MenuButton>
                <MenuList maxH="200px" overflowY="auto" bg={menuBg} borderColor={actionBarBorderColor}>
                  {RR_FILTER_OPTIONS.map((option) => {
                    return (
                      <MenuItem
                        key={option.value}
                        onClick={() => setRrFilter(option.value)}
                        fontSize="xs"
                        fontFamily="'IBM Plex Sans', sans-serif"
                        fontWeight="medium"
                        color={secondaryTextColor}
                        textTransform="uppercase"
                        bg={menuBg}
                        _hover={{ bg: menuHoverBg }}
                      >
                        {option.label}
                      </MenuItem>
                    );
                  })}
                </MenuList>
              </Menu>

              {/* Trade Type Filter */}
              <Menu>
                {/* @ts-ignore - Chakra UI MenuButton with responsive props creates complex union types */}
                <MenuButton
                  as={Button}
                  size="sm"
                  bg={actionBarBg}
                  w={{ base: "100%", md: "150px" } as any}
                  h="32px"
                  px="12px"
                  py="8px"
                  fontSize="xs"
                  fontFamily="'IBM Plex Sans', sans-serif"
                  fontWeight="medium"
                  color={secondaryTextColor}
                  textTransform="uppercase"
                  border="1px solid"
                  borderColor={actionBarBorderColor}
                  borderRadius="md"
                  textAlign="left"
                  justifyContent="space-between"
                  _hover={{ bg: buttonHoverBg }}
                  _active={{ bg: actionBarBg }}
                  _focus={{ bg: actionBarBg, boxShadow: "0 0 0 1px #3EEAEC" }}
                  rightIcon={<Icon as={AiOutlineDown} w={4} h={4} color={secondaryTextColor} />}
                >
                  {TRADE_TYPE_FILTER_OPTIONS.find(opt => opt.value === tradeTypeFilter)?.label || "All Types"}
                </MenuButton>
                <MenuList maxH="200px" overflowY="auto" bg={menuBg} borderColor={actionBarBorderColor}>
                  {TRADE_TYPE_FILTER_OPTIONS.map((option) => {
                    return (
                      <MenuItem
                        key={option.value}
                        onClick={() => setTradeTypeFilter(option.value)}
                        fontSize="xs"
                        fontFamily="'IBM Plex Sans', sans-serif"
                        fontWeight="medium"
                        color={secondaryTextColor}
                        textTransform="uppercase"
                        bg={menuBg}
                        _hover={{ bg: menuHoverBg }}
                      >
                        {option.label}
                      </MenuItem>
                    );
                  })}
                </MenuList>
              </Menu>

              {/* Current market session(s) and open/closed */}
              <CurrentSession instrument={asset.symbol} showLabel={true} />
            </HStack>
          </Box>

          {/* Right: Timer, Stop Button, and How To Use Button */}
          <HStack spacing={3} w={{ base: "100%", md: "auto" }} align="center">
            {signalsEnabled && timeRemainingSeconds !== null && timeRemainingSeconds > 0 && (
              <>
                <Text
                  fontSize="xs"
                  color={secondaryTextColor}
                  fontFamily="'IBM Plex Sans', sans-serif"
                  fontWeight="medium"
                  whiteSpace="nowrap"
                >
                  Setups active for: {formatTimeRemaining(timeRemainingSeconds)}
                </Text>
                <Button
                  onClick={handleStartClick}
                  isLoading={signalsLoading}
                  bgGradient="linear-gradient(135deg, #E00101, #FE4244)"
                  color="white"
                  fontWeight="medium"
                  borderRadius="8px"
                  px="20px"
                  py="10px"
                  h="auto"
                  fontFamily="'IBM Plex Sans', sans-serif"
                  textTransform="uppercase"
                  fontSize="sm"
                  display="inline-flex"
                  alignItems="center"
                  gap="0.5rem"
                  transition="all 0.3s ease"
                  _hover={{
                    pr: "calc(20px + 0.5rem)",
                  }}
                  _active={{
                    bgGradient: "linear-gradient(135deg, #E00101, #FE4244)",
                  }}
                  w={{ base: "100%", md: "auto" }}
                  sx={{
                    "& .button-icon": {
                      width: "1.25rem",
                      height: "1.25rem",
                      transition: "transform 0.3s ease",
                      color: "white",
                    },
                    "&:hover .button-icon": {
                      transform: "translateX(6px)",
                    },
                  }}
                >
                  Stop
                  <Box
                    as="svg"
                    className="button-icon"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5.25 7.5A2.25 2.25 0 0 1 7.5 5.25h9a2.25 2.25 0 0 1 2.25 2.25v9a2.25 2.25 0 0 1-2.25 2.25h-9a2.25 2.25 0 0 1-2.25-2.25v-9Z"
                    />
                  </Box>
                </Button>
              </>
            )}
            <Button
              onClick={onOpen}
              variant="outline"
              borderColor="gray.400"
              color="gray.400"
              size="sm"
              fontSize="xs"
              fontWeight="medium"
              textTransform="uppercase"
              fontFamily="'IBM Plex Sans', sans-serif"
              h="32px"
              minH="32px"
              maxH="32px"
              px={4}
              _hover={{
                borderColor: "gray.500",
                color: "gray.500",
                bg: "transparent",
              }}
              _focus={{
                outline: "none",
                boxShadow: "none",
              }}
              w={{ base: "100%", md: "auto" }}
            >
              How To Use
            </Button>
          </HStack>
        </Flex>
      </Box>

      {/* Two Column Layout - Setups and Bot Performance */}
      <Box 
        mb={4} 
        mx={4} 
        flex="1" 
        minH="0"
        display="flex"
        flexDirection="column"
      >
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4} h="100%" flex="1" minH="0">
            {/* Left Column: Setups (Signals) */}
            {/* @ts-ignore - Chakra UI Card component with responsive props creates complex union types */}
            <Card
              overflow="hidden"
              p="0"
              w="100%"
              bg={cardBg}
              borderRadius="md"
              border="1px solid"
              borderColor={borderColor}
              display="flex"
              flexDirection="column"
              h="100%"
            >
              <Flex
                align="center"
                justify="space-between"
                px={4}
                py={3}
                borderBottom="1px solid"
                borderColor={borderColor}
                flexShrink={0}
              >
                <Text
                  fontSize={{ base: 'xs', sm: 'xs', md: 'sm' }}
                  fontWeight="medium"
                  color="#A0AEC0"
                  fontFamily="'IBM Plex Sans', sans-serif"
                  textTransform="uppercase"
                >
                  Setups
                </Text>
                {filteredSignals.length > 0 && (
                  <Text
                    fontSize="xs"
                    color={labelColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                  >
                    {filteredSignals.length} signal{filteredSignals.length !== 1 ? 's' : ''}
                  </Text>
                )}
              </Flex>
              <CardBody
                p={4}
                overflowY="auto"
                overflowX="visible"
                flex="1"
                minH="0"
                css={{
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "transparent",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: scrollbarThumb,
                    borderRadius: "3px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    background: scrollbarThumbHover,
                  },
                }}
              >
                {!signalsEnabled ? (
                  <Flex
                    align="center"
                    justify="center"
                    h="100%"
                    minH="200px"
                    w="100%"
                    direction="row"
                    gap={2}
                    flexWrap="wrap"
                  >
                    <Text
                      fontSize="sm"
                      color={labelColor}
                      fontFamily="'IBM Plex Sans', sans-serif"
                    >
                      Click
                    </Text>
                    <Button
                      onClick={handleStartClick}
                      isLoading={signalsLoading}
                      bgGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
                      color="white"
                      fontWeight="medium"
                      borderRadius="8px"
                      px="20px"
                      py="10px"
                      h="auto"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      textTransform="uppercase"
                      fontSize="sm"
                      display="inline-flex"
                      alignItems="center"
                      gap="0.5rem"
                      transition="all 0.3s ease"
                      _hover={{
                        pr: "calc(20px + 0.5rem)",
                      }}
                      _active={{
                        bgGradient: "linear-gradient(135deg, #38BBD1, #3EEAEC)",
                      }}
                      sx={{
                        "& .button-icon": {
                          width: "1.25rem",
                          height: "1.25rem",
                          transition: "transform 0.3s ease",
                          color: "white",
                        },
                        "&:hover .button-icon": {
                          transform: "translateX(6px)",
                        },
                      }}
                    >
                      Start
                      <Box
                        as="svg"
                        className="button-icon"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z"
                        />
                      </Box>
                    </Button>
                    <Text
                      fontSize="sm"
                      color={labelColor}
                      fontFamily="'IBM Plex Sans', sans-serif"
                    >
                      to begin receiving setups
                    </Text>
                  </Flex>
                ) : filteredSignals.length === 0 ? (
                  signalsEnabled ? (
                    // Show loading state when signals are enabled but none received yet
                    <SignalLoadingState 
                      labelColor={labelColor} 
                      latestNoSignalReason={latestNoSignalReason}
                    />
                  ) : (
                    // Show filter message if filters are applied
                    <Text
                      fontSize="sm"
                      color={labelColor}
                      textAlign="center"
                      py={8}
                      fontFamily="'IBM Plex Sans', sans-serif"
                    >
                      No signals {rrFilter !== "all" ? `for selected R/R ratio` : ""}
                    </Text>
                  )
                ) : (
                  <VStack align="stretch" spacing={2} w="100%">
                    {filteredSignals.map((signal) => (
                      <Box key={signal.id} w="100%">
                        <SignalCard
                          signal={signal}
                          takenCount={signal.eventId ? (takenCountByEventId[signal.eventId] ?? 0) : 0}
                          onPlaceOrder={(sig) => onPlaceOrder?.(sig, asset)}
                          onClick={(sig) => onSignalClick?.(sig, asset)}
                        />
                      </Box>
                    ))}
                  </VStack>
                )}
              </CardBody>
            </Card>

            {/* Right Column: Bot Performance */}
            {/* @ts-ignore - Chakra UI Card component with responsive props creates complex union types */}
            <Card
              overflow="hidden"
              p="0"
              w="100%"
              bg={cardBg}
              borderRadius="md"
              border="1px solid"
              borderColor={borderColor}
              display="flex"
              flexDirection="column"
              h="100%"
            >
              <Flex
                align="center"
                justify="space-between"
                px={4}
                py={3}
                borderBottom="1px solid"
                borderColor={borderColor}
                flexShrink={0}
              >
                <Text
                  fontSize={{ base: 'xs', sm: 'xs', md: 'sm' }}
                  fontWeight="medium"
                  color="#A0AEC0"
                  fontFamily="'IBM Plex Sans', sans-serif"
                  textTransform="uppercase"
                >
                  System Performance
                </Text>
                <Tooltip
                  label={
                    <Box>
                      <Text fontWeight="bold" mb={2} fontSize="sm">
                        This performance is based on the following system setup:
                      </Text>
                      <Text fontSize="xs" mb={2}>
                        <strong>Max 15% account Risk</strong>
                      </Text>
                      <Text fontSize="xs" mb={1} fontWeight="bold">
                        Lot sizing:
                      </Text>
                      <Text fontSize="xs" mb={0.5}>0-1000: 0.01 (minimum only)</Text>
                      <Text fontSize="xs" mb={0.5}>1001-2500: 0.03 max</Text>
                      <Text fontSize="xs" mb={0.5}>2501-5000: 0.05 max</Text>
                      <Text fontSize="xs" mb={0.5}>5001-10000: 0.1 max</Text>
                      <Text fontSize="xs" mb={0}>{">"}10000: 0.8 max</Text>
                    </Box>
                  }
                  bg={tooltipBg}
                  color={tooltipColor}
                  fontSize="sm"
                  px={3}
                  py={2}
                  borderRadius="md"
                  hasArrow
                  placement="top"
                  whiteSpace="normal"
                  maxW={{ base: "calc(100vw - 32px)", md: "300px" }}
                  shouldWrapChildren
                  closeOnClick={false}
                  closeOnMouseDown={false}
                  modifiers={[
                    {
                      name: "preventOverflow",
                      enabled: true,
                      options: {
                        boundary: "viewport",
                        padding: 16,
                        altAxis: true,
                        altBoundary: true,
                      },
                    },
                    {
                      name: "flip",
                      enabled: true,
                      options: {
                        fallbackPlacements: ["bottom", "left", "right", "top"],
                        padding: 16,
                        boundary: "viewport",
                      },
                    },
                    {
                      name: "offset",
                      options: {
                        offset: [0, 8],
                      },
                    },
                  ]}
                >
                  <Icon
                    as={QuestionIcon}
                    color={secondaryTextColor}
                    w={{ base: '10px', sm: '11px', md: '12px' }}
                    h={{ base: '10px', sm: '11px', md: '12px' }}
                    cursor="help"
                    flexShrink={0}
                  />
                </Tooltip>
              </Flex>
              <CardBody
                p={4}
                overflowY="auto"
                overflowX="visible"
                flex="1"
                minH="0"
                maxH={{ base: "400px", md: "520px" }}
                css={{
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "transparent",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: scrollbarThumb,
                    borderRadius: "3px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    background: scrollbarThumbHover,
                  },
                }}
              >
                <VStack align="stretch" spacing={4} w="100%">
                  {renderSystemPerformancePeriod("Past 1h", systemPerformance?.past_1h ?? null)}
                  {renderSystemPerformancePeriod("Past 4h", systemPerformance?.past_4h ?? null)}
                  {renderSystemPerformancePeriod("Past day", systemPerformance?.past_day ?? null)}
                  {renderSystemPerformancePeriod("Past week", systemPerformance?.past_week ?? null)}
                  {renderBestWorstSection("By hour", "Hour", systemPerformance?.by_hour)}
                  {renderBestWorstSection("By day", "Day", systemPerformance?.by_day)}
                  {renderBestWorstSection("By session", "Session", systemPerformance?.by_session)}
                </VStack>
              </CardBody>
            </Card>
          </SimpleGrid>
      </Box>

      {/* Active Setups Card - Full Width */}
      <Box mx={4} flexShrink={0}>
        <CardBody
          pt={0}
          px={0}
          pb={4}
        >
          {/* @ts-ignore - Chakra UI Card component with responsive props creates complex union types */}
          <Card
            overflow="hidden"
            p="0"
            w="100%"
            bg={cardBg}
            borderRadius="md"
            border="1px solid"
            borderColor={borderColor}
            display="flex"
            flexDirection="column"
          >
            <Flex
              align="center"
              justify="space-between"
              px={4}
              py={3}
              borderBottom="1px solid"
              borderColor={borderColor}
              flexShrink={0}
            >
              <Text
                fontSize={{ base: 'xs', sm: 'xs', md: 'sm' }}
                fontWeight="medium"
                color="#A0AEC0"
                fontFamily="'IBM Plex Sans', sans-serif"
                textTransform="uppercase"
              >
                Active Setups
              </Text>
              <Text
                fontSize="xs"
                color={labelColor}
                fontFamily="'IBM Plex Sans', sans-serif"
                fontWeight="medium"
              >
                {activeTrades.length} trade{activeTrades.length !== 1 ? 's' : ''}
              </Text>
            </Flex>
            <Box overflowX={{ base: "auto", sm: "auto", xl: "hidden" }} overflowY="hidden" style={{ WebkitOverflowScrolling: "touch" }}>
              {/* @ts-ignore - Chakra UI Table component with responsive props creates complex union types */}
              <Table variant='simple' color={textColor} minW={{ base: "800px", xl: "auto" }}>
                <Thead bg={cardBg}>
                  <Tr>
                    <Th
                      color={secondaryTextColor}
                      fontFamily="'IBM Plex Sans', sans-serif"
                      fontWeight="medium"
                      cursor="pointer"
                      _hover={{ bg: buttonHoverBg }}
                    >
                      <HStack spacing={1}>
                        <Text fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">Date</Text>
                        <Icon as={FaSort} w={3} h={3} />
                      </HStack>
                    </Th>
                    <Th color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">Type</Th>
                    <Th color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">Entry</Th>
                    <Th color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">TP</Th>
                    <Th color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">SL</Th>
                    <Th color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">Lot Size</Th>
                    <Th color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">Unrealised P&L</Th>
                    <Th color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">Loss Potential</Th>
                    <Th color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">Profit Potential</Th>
                    <Th color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">Status</Th>
                    <Th color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {activeTrades.length === 0 ? (
                    <Tr>
                      <Td colSpan={11}>
                        <Flex
                          align="center"
                          justify="center"
                          h="100%"
                          minH="150px"
                          w="100%"
                        >
                          <Text
                            fontSize="sm"
                            color={labelColor}
                            textAlign="center"
                            fontFamily="'IBM Plex Sans', sans-serif"
                          >
                            No active setups yet. Take a trade from the setups above to see it here.
                          </Text>
                        </Flex>
                      </Td>
                    </Tr>
                  ) : (
                    <>
                      {/* Total row: sums + Close All Trades */}
                      <Tr bg={alternateRowBg} borderBottomWidth="2px" borderColor={borderColor}>
                        <Td colSpan={6}>
                          <Text fontSize="sm" fontWeight="bold" color={textColor} fontFamily="'IBM Plex Sans', sans-serif">
                            Total
                          </Text>
                        </Td>
                        <Td>
                          <Text
                            fontSize="sm"
                            fontWeight="bold"
                            fontFamily="'IBM Plex Sans', sans-serif"
                            color={activeSetupsTotals.unrealised >= 0 ? "green.500" : "red.500"}
                          >
                            {formatMoney(activeSetupsTotals.unrealised)}
                          </Text>
                        </Td>
                        <Td>
                          <Text fontSize="sm" fontWeight="bold" fontFamily="'IBM Plex Sans', sans-serif" color="red.500">
                            {formatMoney(activeSetupsTotals.lossPotential)}
                          </Text>
                        </Td>
                        <Td>
                          <Text fontSize="sm" fontWeight="bold" fontFamily="'IBM Plex Sans', sans-serif" color="green.500">
                            {formatMoney(activeSetupsTotals.profitPotential)}
                          </Text>
                        </Td>
                        <Td />
                        <Td>
                          {onCloseAllTrades && brokerAccountCode && (
                            <Button
                              size="sm"
                              colorScheme="red"
                              variant="outline"
                              fontFamily="'IBM Plex Sans', sans-serif"
                              fontWeight="medium"
                              onClick={() => onCloseAllTrades()}
                            >
                              Close All Trades
                            </Button>
                          )}
                        </Td>
                      </Tr>
                    {paginatedActiveTrades.map((trade, index) => {
                      const isLong = trade.type === "buy";
                      const formatDate = (date: Date | string): string =>
                        formatTradeDateTimeForDisplay(date, displayTimeZone);
                      const rowBg = index % 2 === 0 ? cardBg : alternateRowBg;
                      
                      return (
                        <Tr
                          key={trade.id}
                          bg={rowBg}
                          _hover={{ bg: buttonHoverBg }}
                        >
                          <Td>
                            <Text fontSize="sm" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                              {formatDate(trade.entryDate)}
                            </Text>
                          </Td>
                          <Td>
                            <Badge
                              bgGradient={isLong
                                ? "linear-gradient(135deg, #06E363, #AAFAB1)"
                                : "linear-gradient(135deg, #E00101, #FE4244)"}
                              color="white"
                              fontSize="xs"
                              fontFamily="'IBM Plex Sans', sans-serif"
                              fontWeight="medium"
                              textTransform="uppercase"
                              p="3px 10px"
                              borderRadius="8px"
                            >
                              {isLong ? "Buy" : "Sell"}
                            </Badge>
                          </Td>
                          <Td>
                            <Text fontSize="sm" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                              {formatMoney(trade.entryPrice)}
                            </Text>
                          </Td>
                          <Td>
                            {trade.takeProfit ? (
                              <Text fontSize="sm" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                                {formatMoney(trade.takeProfit)}
                              </Text>
                            ) : (
                              <Text fontSize="sm" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                                -
                              </Text>
                            )}
                          </Td>
                          <Td>
                            {trade.stopLoss ? (
                              <Text fontSize="sm" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                                {formatMoney(trade.stopLoss)}
                              </Text>
                            ) : (
                              <Text fontSize="sm" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                                -
                              </Text>
                            )}
                          </Td>
                          <Td>
                            {trade.entryQuantity != null ? (
                              <Text fontSize="sm" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                                {trade.entryQuantity}
                              </Text>
                            ) : (
                              <Text fontSize="sm" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                                -
                              </Text>
                            )}
                          </Td>
                          <Td>
                            {(() => {
                              const pnl = trade.floatingPnl ?? trade.netPnL;
                              if (pnl === undefined || pnl === null) {
                                return (
                                  <Text fontSize="sm" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                                    -
                                  </Text>
                                );
                              }
                              return (
                                <Text
                                  fontSize="sm"
                                  fontWeight="medium"
                                  fontFamily="'IBM Plex Sans', sans-serif"
                                  color={pnl >= 0 ? "green.500" : "red.500"}
                                >
                                  {formatMoney(pnl)}
                                </Text>
                              );
                            })()}
                          </Td>
                          <Td>
                            {trade.lossPotential != null ? (
                              <Text
                                fontSize="sm"
                                fontWeight="medium"
                                fontFamily="'IBM Plex Sans', sans-serif"
                                color="red.500"
                              >
                                {formatMoney(trade.lossPotential)}
                              </Text>
                            ) : (
                              <Text fontSize="sm" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                                -
                              </Text>
                            )}
                          </Td>
                          <Td>
                            {trade.profitPotential != null ? (
                              <Text
                                fontSize="sm"
                                fontWeight="medium"
                                fontFamily="'IBM Plex Sans', sans-serif"
                                color="green.500"
                              >
                                {formatMoney(trade.profitPotential)}
                              </Text>
                            ) : (
                              <Text fontSize="sm" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                                -
                              </Text>
                            )}
                          </Td>
                          <Td>
                            <Badge
                              bgGradient="linear-gradient(135deg, #F45B0B, #F49113)"
                              color="white"
                              fontSize="xs"
                              fontFamily="'IBM Plex Sans', sans-serif"
                              fontWeight="medium"
                              textTransform="uppercase"
                              p="3px 10px"
                              borderRadius="8px"
                            >
                              Open
                            </Badge>
                          </Td>
                          <Td>
                            {onCloseTrade && (
                              <Badge
                                as="button"
                                bgGradient="linear-gradient(135deg, #E00101, #FE4244)"
                                color="white"
                                fontSize="xs"
                                fontFamily="'IBM Plex Sans', sans-serif"
                                fontWeight="medium"
                                textTransform="uppercase"
                                p="3px 10px"
                                borderRadius="8px"
                                cursor="pointer"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onCloseTrade(trade.id);
                                }}
                                _hover={{
                                  opacity: 0.9,
                                }}
                              >
                                Close Trade
                              </Badge>
                            )}
                          </Td>
                        </Tr>
                      );
                    })}
                    </>
                  )}
                </Tbody>
              </Table>
            </Box>
            {activeTrades.length > ACTIVE_SETUPS_PAGE_SIZE && (
              <Flex
                justify="space-between"
                align="center"
                px={4}
                py={3}
                borderTop="1px solid"
                borderColor={borderColor}
                flexWrap="wrap"
                gap={2}
              >
                <Text fontSize="sm" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif">
                  {activeTrades.length} trade{activeTrades.length !== 1 ? "s" : ""} total
                </Text>
                <HStack spacing={2}>
                  <Button
                    size="sm"
                    variant="outline"
                    borderColor={borderColor}
                    color={textColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    isDisabled={activeSetupsPage <= 1}
                    onClick={() => setActiveSetupsPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <Text fontSize="sm" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" whiteSpace="nowrap">
                    Page {activeSetupsPage} of {activeSetupsTotalPages}
                  </Text>
                  <Button
                    size="sm"
                    variant="outline"
                    borderColor={borderColor}
                    color={textColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    isDisabled={activeSetupsPage >= activeSetupsTotalPages}
                    onClick={() => setActiveSetupsPage((p) => Math.min(activeSetupsTotalPages, p + 1))}
                  >
                    Next
                  </Button>
                </HStack>
              </Flex>
            )}
          </Card>
        </CardBody>
      </Box>

      <MarketOverviewCard
        instrument={asset.symbol}
        dataFromWebSocket={signalsContext?.marketOverviewByInstrument?.[asset.symbol] ?? undefined}
      />

      <HowToUseModal isOpen={isOpen} onClose={onClose} />
    </Card>
  );
};

export default SignalStreamCard;
